import { z } from 'zod'
import { roleSchema } from './role.js'

/**
 * HR Org Chart — mirrors services/api/src/schemas/org_chart.py.
 *
 * GET /api/v1/hr/org-chart returns the full assignment graph for the
 * HR / Director / Exec Director read-only org chart at /hr/org-chart
 * (Track B MVP). Read-only — no request bodies on this surface.
 */

export const approvalScopeTypeSchema = z.enum(['employee', 'team'])
export type ApprovalScopeType = z.infer<typeof approvalScopeTypeSchema>

/** Kind of node in the structural org tree (`org_units.unit_type`).
 *  HR's structure is company → function → department → team (teams are the
 *  separate leaf table). A label, not a depth cap — the tree nests to
 *  arbitrary depth. */
export const orgUnitTypeSchema = z.enum([
  'company',
  'function',
  'department',
])
export type OrgUnitType = z.infer<typeof orgUnitTypeSchema>

/** A node in the recursive structural org tree (`org_units`). `parentId`
 *  is the unit above (null only for the single root company). Structural
 *  only — does NOT drive approver routing. */
export const orgChartUnitSchema = z.object({
  id: z.guid(),
  parentId: z.guid().nullable(),
  unitType: orgUnitTypeSchema,
  name: z.string(),
  headEmployeeId: z.guid().nullable(),
})
export type OrgChartUnit = z.infer<typeof orgChartUnitSchema>

/** A team — the leaf of the org tree. `parentUnitId` is the unit it hangs
 *  under (null = not yet placed); `managerId` is the team lead. */
export const orgChartTeamSchema = z.object({
  id: z.guid(),
  name: z.string(),
  parentUnitId: z.guid().nullable(),
  managerId: z.guid().nullable(),
})
export type OrgChartTeam = z.infer<typeof orgChartTeamSchema>

export const approvalAssignmentTypeSchema = z.enum([
  'primary',
  'backup',
  'hod_oversight',
  'temporary_cover',
])
export type ApprovalAssignmentType = z.infer<typeof approvalAssignmentTypeSchema>

export const approvalSourceSchema = z.enum([
  'migrated_from_reports_to',
  'migrated_from_managed_teams',
  'migrated_from_delegation',
  'hr_grant',
  'manager_self_serve',
  'system',
])
export type ApprovalSource = z.infer<typeof approvalSourceSchema>

/** A single employee node on the org chart. `role` is never 'system' —
 *  the route filters the sentinel out before the wire shape is built. */
export const orgChartEmployeeSchema = z.object({
  id: z.guid(),
  name: z.string(),
  email: z.string(),
  teamId: z.guid(),
  teamName: z.string(),
  role: roleSchema,
  jobTitle: z.string(),
  active: z.boolean(),
  // Decorative HR labels — used by AddEmployeeModal to prefill the
  // primary-approver picker with the chosen team's manager / HOD.
  isManager: z.boolean(),
  isHod: z.boolean(),
})
export type OrgChartEmployee = z.infer<typeof orgChartEmployeeSchema>

/** One row in approval_assignments, denormalized for display. Only
 *  currently-effective non-revoked rows make it onto the wire. */
export const orgChartAssignmentSchema = z.object({
  id: z.guid(),
  approverId: z.guid(),
  approverName: z.string(),
  approverRole: roleSchema,
  scopeType: approvalScopeTypeSchema,
  scopeEmployeeId: z.guid().nullable(),
  scopeTeamId: z.guid().nullable(),
  scopeTeamName: z.string().nullable(),
  assignmentType: approvalAssignmentTypeSchema,
  source: approvalSourceSchema,
  effectiveFrom: z.string(),
  effectiveTo: z.string().nullable(),
  scopeLeaveTypes: z.array(z.string()),
})
export type OrgChartAssignment = z.infer<typeof orgChartAssignmentSchema>

/** Full org chart payload returned by GET /api/v1/hr/org-chart.
 *  `orgUnits` + `teams` describe the STRUCTURAL tree (employee → team →
 *  parent-unit chain → root); the remaining fields are the pre-existing
 *  approval-graph shape, unchanged — this payload is a superset. */
export const orgChartOutSchema = z.object({
  orgUnits: z.array(orgChartUnitSchema),
  teams: z.array(orgChartTeamSchema),
  employees: z.array(orgChartEmployeeSchema),
  assignments: z.array(orgChartAssignmentSchema),
  /** Employee id (string) → standing PRIMARY approver id, or null when
   *  the employee is top-of-org. Every employee in `employees` appears
   *  as a key, so the FE can iterate without a fallback. */
  primaryApproverByEmployee: z.record(z.string(), z.guid().nullable()),
  /** Approver id (string) → list of employee ids whose standing PRIMARY
   *  they are. Approvers with no direct reports are absent (sparse map). */
  directReportsByApprover: z.record(z.string(), z.array(z.guid())),
})
export type OrgChartOut = z.infer<typeof orgChartOutSchema>
